import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';

// Same events the eligibility matrix hides (team relay + kulai eduthal are not
// individually placed), so the scoring grid mirrors athleticsregistration/matrix.
const EXCLUDED_EVENT_READABLE_IDS = new Set<string>(['stafett_100_x_4', 'kulai_eduthal', 'kili_thattu']);

// One cell of the grid is keyed by participant row id + event id.
const cellKey = (regId: number, eventId: number) => `${regId}:${eventId}`;

// Working/baseline maps store the place as a string ('' | '1' | '2' | '3').
type ScoreMap = Record<string, string>;

const cellStyle: React.CSSProperties = { border: '1px solid #888', padding: '6px', textAlign: 'center', fontSize: '.9rem' };
const headStyle: React.CSSProperties = {
  ...cellStyle, background: '#eee', whiteSpace: 'normal',
  position: 'sticky', top: 0, zIndex: 2,
};
// ODS-styled input (ods-input__input); we only override width so it stays narrow.
const placeInputStyle: React.CSSProperties = {
  width: '2.6rem', textAlign: 'center', padding: '2px', margin: 0,
};

// Points awarded per placement; only SAVED placements count toward the team totals.
const PLACE_POINTS: Record<string, number> = { '1': 5, '2': 3, '3': 1 };

// Champion (அதிசிறந்த வீரர்) rule, from the Sportsregler: a competitor is a champion if they
// took part in THREE individual events with a track/field mix (1 field + 2 track, or 1 track +
// 2 field), placed in all three, and earned at least 13 points. Each event is classified by its
// ReadableId; ball_plukk, sitronlop and the team events are excluded from the rule per APTAK.
const TRACK_EVENT_READABLE_IDS = new Set<string>(['40_lop', '60_lop', '80_lop', '100_lop', '200_lop', '400_lop']);
const FIELD_EVENT_READABLE_IDS = new Set<string>(['lengdehopp', 'kulekast', 'spydkast']);
const CHAMPION_MIN_POINTS = 13;

const cardStyle: React.CSSProperties = {
  border: '1px solid #ccc', borderRadius: 6, padding: '0.75rem 1rem', height: '100%',
};

const formatDateTime = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('nb-NO') + ' ' + d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

// Audit snapshots are flat JSON: { Firstname, Lastname, "<event name>": place, ... }.
const parseSnapshot = (json?: string): Record<string, any> => {
  try { return JSON.parse(json || '{}'); } catch { return {}; }
};
const changeName = (e: Api.LocalAdminAuditLog): string => {
  const o = parseSnapshot(e.NewValues || e.OldValues);
  return `${o.Firstname || ''} ${o.Lastname || ''}`.trim() || '–';
};
// Per-event "before → after" lines for the changes that actually differ.
const changeSummary = (e: Api.LocalAdminAuditLog): string[] => {
  const o = parseSnapshot(e.OldValues), n = parseSnapshot(e.NewValues);
  const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]))
    .filter(k => k !== 'Firstname' && k !== 'Lastname');
  const lines: string[] = [];
  keys.forEach(k => {
    const ov = o[k] == null ? '' : String(o[k]);
    const nv = n[k] == null ? '' : String(n[k]);
    if (ov !== nv) lines.push(`${k}: ${ov || '–'} → ${nv || '–'}`);
  });
  return lines;
};

const AthleticsScoresMatrixView: React.FC<RouteComponentProps> = () => {
  const [events, setEvents] = useState<Api.AthleticsEventDto[]>([]);
  const [participants, setParticipants] = useState<Api.AthleticsRegistrationDto[]>([]);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [scores, setScores] = useState<ScoreMap>({});      // editable state
  const [baseline, setBaseline] = useState<ScoreMap>({});   // last saved state
  const [changes, setChanges] = useState<Api.LocalAdminAuditLog[]>([]); // last 10 saved changes
  const [allChanges, setAllChanges] = useState<Api.LocalAdminAuditLog[]>([]); // full list for the modal
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const loadChanges = async () => {
    try {
      const data = await RestService.get<Api.LocalAdminAuditLog[]>('/api/athleticsregistration/GetScoreChanges?take=10');
      setChanges(data ?? []);
    } catch { /* best effort — the panel just stays empty */ }
  };

  // Open the modal and (re)fetch the full change list (capped at 100 server-side).
  const openAllChanges = async () => {
    setShowAllChanges(true);
    try {
      const data = await RestService.get<Api.LocalAdminAuditLog[]>('/api/athleticsregistration/GetScoreChanges?take=100');
      setAllChanges(data ?? []);
    } catch { /* best effort — the modal just shows what it has */ }
  };

  useEffect(() => {
    document.title = 'Registrer resultater — Annaipoopathi';
    (async () => {
      try {
        const year = new Date().getFullYear();
        const [evts, rows, savedScores, depts] = await Promise.all([
          RestService.get<Api.AthleticsEventDto[]>(`/api/athleticsregistration/GetEvents?year=${year}`),
          RestService.get<Api.AthleticsRegistrationDto[]>('/api/athleticsregistration/All'),
          RestService.get<Api.AthleticsScoreDto[]>(`/api/athleticsregistration/GetScores?year=${year}`),
          RestService.get<Api.Department[]>('/api/examregistration/GetDepartments'),
        ]);
        setEvents((evts ?? []).filter(e => !EXCLUDED_EVENT_READABLE_IDS.has(e.ReadableId)));
        setParticipants(rows ?? []);
        setDepartments(depts ?? []);
        const loaded: ScoreMap = {};
        (savedScores ?? []).forEach(s => { loaded[cellKey(s.AthleticsRegistrationId, s.AthleticsEventId)] = String(s.Place); });
        setScores(loaded);
        setBaseline(loaded);
        await loadChanges();
      } catch (e) {
        toast.error(`Kunne ikke laste data: ${e}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const fullName = (p: Api.AthleticsRegistrationDto) => `${p.Firstname ?? ''} ${p.Lastname ?? ''}`.trim();
  const tamilName = (p: Api.AthleticsRegistrationDto) => `${p.FirstnameTamil ?? ''} ${p.LastnameTamil ?? ''}`.trim();

  const birthYear = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : String(d.getFullYear());
  };

  const departmentName = (id?: number) => departments.find(d => d.Id === id)?.Name ?? '';

  // Team totals from SAVED placements only (baseline), so points never move on an
  // unsaved edit. House: "gul" → yellow team, "rød" → red team.
  const teamPoints = useMemo(() => {
    const houseByReg = new Map(participants.map(p => [p.Id, p.House]));
    let gul = 0, rod = 0;
    Object.entries(baseline).forEach(([key, place]) => {
      const pts = PLACE_POINTS[place] ?? 0;
      if (!pts) return;
      const house = houseByReg.get(Number(key.split(':')[0]));
      if (house === 'gul') gul += pts;
      else if (house === 'rød') rod += pts;
    });
    return { gul, rod };
  }, [baseline, participants]);

  // Points per department, also from saved placements only, highest first.
  const deptPoints = useMemo(() => {
    const deptByReg = new Map(participants.map(p => [p.Id, p.DepartmentId]));
    const totals = new Map<number, number>();
    Object.entries(baseline).forEach(([key, place]) => {
      const pts = PLACE_POINTS[place] ?? 0;
      if (!pts) return;
      const deptId = deptByReg.get(Number(key.split(':')[0]));
      if (deptId == null) return;
      totals.set(deptId, (totals.get(deptId) ?? 0) + pts);
    });
    return Array.from(totals.entries())
      .map(([id, pts]) => ({ name: departmentName(id), pts }))
      .sort((a, b) => b.pts - a.pts);
  }, [baseline, participants, departments]);

  // Champions (அதிசிறந்த வீரர்), from SAVED placements only. A participant qualifies when they
  // placed in exactly three rule-eligible individual events that mix track and field (≥1 of each),
  // totalling at least 13 points. Sorted by points (then name).
  const champions = useMemo(() => {
    // Map each event id to its category for the rule (skips excluded events).
    const categoryByEvent = new Map<number, 'track' | 'field'>();
    events.forEach(e => {
      if (TRACK_EVENT_READABLE_IDS.has(e.ReadableId)) categoryByEvent.set(e.Id, 'track');
      else if (FIELD_EVENT_READABLE_IDS.has(e.ReadableId)) categoryByEvent.set(e.Id, 'field');
    });

    // Aggregate each participant's qualifying placements.
    const perReg = new Map<number, { track: number; field: number; points: number }>();
    Object.entries(baseline).forEach(([key, place]) => {
      const pts = PLACE_POINTS[place] ?? 0;
      if (!pts) return; // only actual placements (1/2/3) count
      const [regStr, evStr] = key.split(':');
      const category = categoryByEvent.get(Number(evStr));
      if (!category) return; // event excluded from the rule
      const agg = perReg.get(Number(regStr)) ?? { track: 0, field: 0, points: 0 };
      if (category === 'track') agg.track += 1; else agg.field += 1;
      agg.points += pts;
      perReg.set(Number(regStr), agg);
    });

    return participants
      .map(p => ({ p, agg: perReg.get(p.Id) }))
      .filter(x => x.agg
        && x.agg.track + x.agg.field === 3   // three qualifying events…
        && x.agg.track >= 1 && x.agg.field >= 1   // …with both track and field
        && x.agg.points >= CHAMPION_MIN_POINTS)
      .map(x => ({ p: x.p, points: x.agg!.points }))
      .sort((a, b) => b.points - a.points || fullName(a.p).localeCompare(fullName(b.p)));
  }, [baseline, participants, events]);

  // UtøverID, e.g. "V-001": first letter of the department's distinctive (last) word —
  // every name is "Annai <X>" — followed by the id zero-padded to 3 digits. Matches the
  // server-side UtoverId helper used for bibs/diplomas/dommerliste.
  const utoverId = (p: Api.AthleticsRegistrationDto): string => {
    const parts = departmentName(p.DepartmentId).split(' ').filter(Boolean);
    const letter = parts.length ? parts[parts.length - 1][0].toUpperCase() : '';
    return `${letter}-${String(p.Id).padStart(3, '0')}`;
  };

  // Sort key for birth year as a number (0 when unknown, so it sinks to the bottom).
  const birthYearNum = (iso?: string): number => {
    if (!iso) return 0;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 0 : d.getFullYear();
  };
  // Girls before boys within the same year; unknown gender last.
  const genderRank = (g?: string): number => (g === 'girl' ? 0 : g === 'boy' ? 1 : 2);

  const filteredParticipants = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q
      ? participants
      : participants.filter(p =>
          String(p.Id).includes(q) ||
          utoverId(p).toLowerCase().includes(q) ||
          fullName(p).toLowerCase().includes(q) ||
          tamilName(p).includes(q));
    // Youngest first (highest birth year on top), then girls before boys.
    return [...base].sort((a, b) =>
      birthYearNum(b.DateOfBirth) - birthYearNum(a.DateOfBirth) ||
      genderRank(a.Gender) - genderRank(b.Gender) ||
      fullName(a).localeCompare(fullName(b), 'nb'));
  }, [participants, departments, search]);

  // Row tint by team (house): "gul" → light yellow, "rød" → light red.
  const rowBackground = (house?: string): string | undefined =>
    house === 'gul' ? '#fff8d6' : house === 'rød' ? '#fde2e0' : undefined;

  // A row is dirty when any of its (registered) cells differs from what was saved.
  const isRowDirty = (p: Api.AthleticsRegistrationDto): boolean =>
    events.some(e => {
      if (!(p.EventIds ?? []).includes(e.Id)) return false;
      const k = cellKey(p.Id, e.Id);
      return (scores[k] ?? '') !== (baseline[k] ?? '');
    });

  // Accept only an empty value or a single 1/2/3.
  const onCellChange = (regId: number, eventId: number, raw: string) => {
    const v = raw.trim();
    if (v !== '' && v !== '1' && v !== '2' && v !== '3') return;
    setScores(prev => ({ ...prev, [cellKey(regId, eventId)]: v }));
  };

  const saveRow = async (p: Api.AthleticsRegistrationDto) => {
    setSavingId(p.Id);
    try {
      const year = new Date().getFullYear();
      const entries: Api.AthleticsScoreEntry[] = events
        .filter(e => (p.EventIds ?? []).includes(e.Id))
        .map(e => {
          const v = scores[cellKey(p.Id, e.Id)] ?? '';
          return { AthleticsEventId: e.Id, Place: v === '' ? undefined : parseInt(v, 10) };
        });
      await RestService.post<Api.SaveAthleticsScoresRequest, { Message: string }>(
        '/api/athleticsregistration/SaveScores',
        { AthleticsRegistrationId: p.Id, Year: year, Scores: entries },
      );
      // Re-baseline just this row's cells so the Lagre button greys out again.
      setBaseline(prev => {
        const next = { ...prev };
        events.filter(e => (p.EventIds ?? []).includes(e.Id)).forEach(e => {
          const k = cellKey(p.Id, e.Id);
          next[k] = scores[k] ?? '';
        });
        return next;
      });
      await loadChanges();
      toast.success('Resultater lagret');
    } catch (e) {
      toast.error(`Kunne ikke lagre: ${e}`);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <OKomBreadcrumb path='Friidrett, Registrer resultater'></OKomBreadcrumb>
      {isLoading && <OKomLoading text="Laster..." />}
      <div className="ods-grid ods-grid--gap ods-grid--center" style={{ maxWidth: '100%' }}>
        {/* Column 1 intentionally empty */}
        <div className="ods-grid__column--4"></div>

        {/* Column 2 — team points (from saved results only) */}
        <div className="ods-grid__column--4">
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Poengsammendrag</h3>
            <div style={{ display: 'flex', gap: '2rem', fontSize: '1.15rem' }}>
              <div><span style={{ color: '#d4a200' }}>●</span> Gult lag: <strong>{teamPoints.gul}</strong></div>
              <div><span style={{ color: '#c0392b' }}>●</span> Rødt lag: <strong>{teamPoints.rod}</strong></div>
            </div>
            {deptPoints.length > 0 && (
              <div style={{ marginTop: '.6rem', borderTop: '1px solid #eee', paddingTop: '.5rem' }}>
                <div className="ods-color-text-grayscale-10" style={{ fontSize: '.8rem', marginBottom: '.25rem' }}>Per avdeling</div>
                {deptPoints.map(d => (
                  <div key={d.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem' }}>
                    <span>{d.name || '–'}</span><strong>{d.pts}</strong>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '.6rem', borderTop: '1px solid #eee', paddingTop: '.5rem' }}>
              <div className="ods-color-text-grayscale-10" style={{ fontSize: '.8rem', marginBottom: '.25rem' }}>
                Champions (≥{CHAMPION_MIN_POINTS} p · 3 øvelser med både løp og felt)
              </div>
              {champions.length === 0 ? (
                <div className="ods-color-text-grayscale-10" style={{ fontSize: '.85rem' }}>Ingen ennå.</div>
              ) : (
                champions.map(c => (
                  <div key={c.p.Id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem' }}>
                    <span>🏆 {fullName(c.p)} <span className="ods-color-text-grayscale-10">({utoverId(c.p)})</span></span>
                    <strong>{c.points} p</strong>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Column 3 — the last 10 saved changes */}
        <div className="ods-grid__column--4">
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Siste endringer</h3>
              <button
                type="button"
                className="ods-button ods-button--small ods-button--outline"
                onClick={openAllChanges}
              >
                Vis alle
              </button>
            </div>
            {changes.length === 0 ? (
              <p className="ods-color-text-grayscale-10" style={{ margin: 0 }}>Ingen endringer ennå.</p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: '9rem', overflowY: 'auto' }}>
                {changes.map(c => (
                  <li key={c.Id} style={{ borderBottom: '1px solid #eee', padding: '.3rem 0', fontSize: '.8rem' }}>
                    <div>
                      <strong>{changeName(c)}</strong>{' '}
                      <span className="ods-color-text-grayscale-10">{formatDateTime(c.CreatedDate)}</span>
                    </div>
                    {changeSummary(c).map((line, i) => (<div key={i}>{line}</div>))}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="ods-grid__column--12">
          <label className="ods-input__label" style={{ maxWidth: 360 }}>
            Søk (navn eller ID)
            <input
              className="ods-input__input"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Søk på deltakernavn eller rad-ID..."
            />
          </label>
        </div>

        <div className="ods-grid__column--12" style={{ maxHeight: '75vh', overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', background: '#fff', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, textAlign: 'left' }}>ID</th>
                <th style={{ ...headStyle, textAlign: 'left' }}>Deltaker</th>
                <th style={{ ...headStyle, textAlign: 'left' }}>År</th>
                {events.map(e => (
                  <th key={e.Id} style={headStyle}>
                    <div>{e.NameNo}</div>
                    <div style={{ fontWeight: 400, fontSize: '.875rem' }}>{e.NameTa}</div>
                  </th>
                ))}
                <th style={{ ...headStyle, textAlign: 'center' }}>Handling</th>
              </tr>
            </thead>
            <tbody>
              {filteredParticipants.map(p => {
                const dirty = isRowDirty(p);
                const saving = savingId === p.Id;
                return (
                  <tr key={p.Id} style={{ background: rowBackground(p.House) }}>
                    <td style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>{utoverId(p)}</td>
                    <td style={{ ...cellStyle, textAlign: 'left', whiteSpace: 'nowrap' }}>{fullName(p)}</td>
                    <td style={{ ...cellStyle, textAlign: 'left' }}>{birthYear(p.DateOfBirth)}</td>
                    {events.map(e => {
                      const registered = (p.EventIds ?? []).includes(e.Id);
                      return (
                        <td key={e.Id} style={cellStyle}>
                          {registered && (
                            <input
                              className="ods-input__input"
                              style={placeInputStyle}
                              type="text"
                              inputMode="numeric"
                              maxLength={1}
                              value={scores[cellKey(p.Id, e.Id)] ?? ''}
                              onChange={ev => onCellChange(p.Id, e.Id, ev.target.value)}
                              title="1, 2 eller 3"
                            />
                          )}
                        </td>
                      );
                    })}
                    <td style={cellStyle}>
                      <button
                        type="button"
                        className="ods-button ods-button--small"
                        disabled={!dirty || saving}
                        onClick={() => saveRow(p)}
                      >
                        Lagre
                        {saving && (
                          <span className="ods-margin-left-1" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                            <BeatLoader color="#ffffff" size={6} />
                          </span>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredParticipants.length === 0 && !isLoading && (
                <tr>
                  <td style={cellStyle} colSpan={events.length + 4}>
                    {participants.length === 0 ? 'Ingen deltakere.' : 'Ingen treff for søket.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <OKomModal
        open={showAllChanges}
        onClose={() => setShowAllChanges(false)}
        ariaLabel="Alle endringer"
        popupStyle={{ maxWidth: '50rem', width: '90%' }}
      >
        <h2 style={{ marginTop: 0 }}>Alle endringer</h2>
        {allChanges.length === 0 ? (
          <p className="ods-color-text-grayscale-10">Ingen endringer.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.9rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '.4rem .6rem', borderBottom: '2px solid #ccc', whiteSpace: 'nowrap' }}>Tidspunkt</th>
                <th style={{ textAlign: 'left', padding: '.4rem .6rem', borderBottom: '2px solid #ccc' }}>Endret av</th>
                <th style={{ textAlign: 'left', padding: '.4rem .6rem', borderBottom: '2px solid #ccc' }}>Deltaker</th>
                <th style={{ textAlign: 'left', padding: '.4rem .6rem', borderBottom: '2px solid #ccc' }}>Endringer</th>
              </tr>
            </thead>
            <tbody>
              {allChanges.map(c => (
                <tr key={c.Id}>
                  <td style={{ padding: '.4rem .6rem', borderBottom: '1px solid #eee', whiteSpace: 'nowrap', verticalAlign: 'top' }}>{formatDateTime(c.CreatedDate)}</td>
                  <td style={{ padding: '.4rem .6rem', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>{c.UserEmail || `#${c.UserId}`}</td>
                  <td style={{ padding: '.4rem .6rem', borderBottom: '1px solid #eee', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{changeName(c)}</td>
                  <td style={{ padding: '.4rem .6rem', borderBottom: '1px solid #eee', verticalAlign: 'top' }}>
                    {changeSummary(c).map((line, i) => (<div key={i}>{line}</div>))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </OKomModal>
    </>
  );
};

export default withRouter(AthleticsScoresMatrixView);
